import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
    useMediaQuery
} from "@mui/material";
import {
    OrganizationType,
    PermissionResourceType,
    PermissionType,
    Property
} from "../../models";
import {
    isDesktop,
    useMobileOrientation
} from "react-device-detect";
import {
    propertyFormIsValidSelector,
    propertyFormValidationMapAtom,
    propertyFormValueAtom
} from "../../lib/property/state/PropertyFormRecoilState";
import {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    useRecoilState,
    useRecoilValue,
    useResetRecoilState,
    useSetRecoilState
} from "recoil";

import ArchiveIcon from '@mui/icons-material/Archive';
import ArchivePropertyDialog from "./ArchivePropertyDialog";
import { AttachmentImageCarousel } from "../image/AttachmentImageCarousel";
import ClientLogger from "../../lib/logging/ClientLogger";
import ClientLoggerFactory from "../../lib/logging/ClientLoggerFactory";
import { CodeBuildJobList } from "../../components/codeBuildJob/CodeBuildJobList";
import DataStorePropertyDAOFactory from "../../lib/property/dao/datastore/DataStorePropertyDAOFactory";
import DropDownMenu from "../../components/menu/DropDownMenu";
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ManageResourcePermissionDialog } from "../permission/ManageResourcePermissionDialog";
import { PermissionRestrictedButton } from "../../components/general/button/PermissionRestrictedButton";
import { PermissionRestrictedControl } from "../../components/general/button/PermissionRestrictedControl";
import PropertyAttribute from "./PropertyAttribute";
import PropertyAttributeForm from "./PropertyAttributeForm";
import PropertyDAO from "../../lib/property/dao/PropertyDAO";
import { RecordingList } from "../../components/chime/RecordingList";
import { ShareIcon } from "../../components/icons";
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import _ from "lodash";
import globalStyle from "../../assets/style/globalStyle";
import { propertyIdToPropertySelectorFamily } from "../../lib/property/state/PropertyRecoilState";
import theme from "../../assets/style/theme";
import { useTeamManagement } from "../../pages/teamManagement/useTeamManagement";
import { useUser } from "../../components/user/UserContext";

type PropertyInfoProps = {
    readonly propertyId: string;
};

const PropertyInfo = (props: PropertyInfoProps) => {
    const COMPONENT_NAME = "PropertyInfo";
    /* Internal page state */
    const { propertyId } = props;
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isOpenShareDialog, setIsOpenShareDialog] = useState<boolean>(false);
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState<boolean>(false);

    const [property, setProperty] = useRecoilState<Property | null>(propertyIdToPropertySelectorFamily(propertyId));
    const [formState, setFormState] = useRecoilState<Property | undefined>(propertyFormValueAtom);
    const setFormValidationMap = useSetRecoilState<Map<keyof Property, boolean>>(propertyFormValidationMapAtom);
    const isFormValid = useRecoilValue<boolean>(propertyFormIsValidSelector);
    const resetFormState = useResetRecoilState(propertyFormValueAtom);
    const resetFormValidationMap = useResetRecoilState(propertyFormValidationMapAtom);

    /* Dependencies */
    const propertyDaoRef = useRef<PropertyDAO>(DataStorePropertyDAOFactory.getInstance());
    const loggerRef = useRef<ClientLogger>(ClientLoggerFactory.getClientLogger(COMPONENT_NAME));
    const globalClasses = globalStyle();
    const isScreenSizeSmallerThanMD = useMediaQuery(theme.breakpoints.down("md"));
    const { isPortrait } = useMobileOrientation();
    const { isAccountOwner } = useTeamManagement();
    const { accountType } = useUser();

    const isOneColumnLayout = useMemo(() => {
        if (isDesktop) {
            return isScreenSizeSmallerThanMD;
        }
        return isPortrait;
    }, [isDesktop, isPortrait, isScreenSizeSmallerThanMD]);

    useEffect(() => () => {
        resetRecoilStates();
    }, []);

    const resetRecoilStates = () => {
        resetFormState();
        resetFormValidationMap();
    };

    const onEdit = () => {
        setFormState(property!);
        setFormValidationMap((original) => {
            const updated: Map<keyof Property, boolean> = new Map(original);
            updated.set("address", !!property?.address);
            return updated;
        });
        setIsEditMode(true);
    };

    const onSave = async () => {
        const existingProperty: Property = property!;
        try {
            if (_.isEqual(existingProperty, formState)) {
                setIsEditMode(false);
                return;
            }
            if (formState) {
                const updatedProperty = await propertyDaoRef.current.editProperty(propertyId, formState);
                setProperty(updatedProperty);
                setFormState(updatedProperty);
            }
            setIsEditMode(false);
        } catch (error) {
            loggerRef.current.error(
                "Failed to update the property",
                error,
                ["PropertyUpdateFailure"]
            );
            setProperty(existingProperty);
            setFormState(existingProperty);
            setIsEditMode(false);
        }
    };

    return (
        <>
            <Box
                display="grid"
                gridTemplateColumns={isOneColumnLayout ? "1fr" : `repeat(2, minmax(0, ${theme.breakpoints.values.md}px))`}
                justifyContent="space-around"
                gap={2}
            >
                <Box height={'100%'} display="flex" flexDirection="column" gap={1}>
                    <Box flexShrink={0} height={theme.spacing(4)}>
                        <Typography variant="h6">
                            Property Image
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: {
                                xs: "200px",
                                sm: "370px"
                            },
                            aspectRatio: "16/9",
                            overflow: "hidden"
                        }}
                    >
                        <AttachmentImageCarousel attachmentParent={{ id: propertyId, type: PermissionResourceType.PROPERTY }} />
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={{ xs: 1, sm: 2 }}>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">
                            Property Information
                        </Typography>
                        <DropDownMenu
                            menuIconVariant={globalClasses.outlinedMenuButton}
                            menuItems={[
                                <PermissionRestrictedControl
                                    resourceType={PermissionResourceType.PROPERTY}
                                    requiredPermission={PermissionType.SHARE_PERMISSION}
                                    resourceId={propertyId}
                                >
                                    {(isDisabled) => (
                                        <MenuItem
                                            onClick={() => {
                                                setIsOpenShareDialog(true);
                                            }}
                                            disabled={isDisabled}
                                        >
                                            <ListItemIcon>
                                                <ShareIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                                Share Property
                                            </ListItemText>
                                        </MenuItem>
                                    )}
                                </PermissionRestrictedControl>,
                                ...(accountType !== OrganizationType.PROPERTY_MANAGEMENT || isAccountOwner ? [
                                    <PermissionRestrictedControl
                                        resourceType={PermissionResourceType.PROPERTY}
                                        requiredPermission={PermissionType.EDIT}
                                        resourceId={propertyId}
                                    >
                                        {(isDisabled) => (
                                            property?.isArchived ? (
                                                <MenuItem
                                                    onClick={async () => {
                                                        const updatedProperty = await propertyDaoRef.current.editProperty(propertyId, { ...property!, isArchived: false });
                                                        setProperty(updatedProperty);
                                                        setFormState(updatedProperty);
                                                    }}
                                                    disabled={isDisabled}
                                                >
                                                    <ListItemIcon>
                                                        <UnarchiveIcon />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        Restore Property
                                                    </ListItemText>
                                                </MenuItem>
                                            ) : (
                                                <MenuItem
                                                    onClick={async () => {
                                                        setIsArchiveDialogOpen(true);
                                                    }}
                                                    disabled={isDisabled}
                                                >
                                                    <ListItemIcon>
                                                        <ArchiveIcon />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        Archive Property
                                                    </ListItemText>
                                                </MenuItem>
                                            )
                                        )}
                                    </PermissionRestrictedControl>
                                ] : [])
                            ]}
                        />
                    </Box>
                    {isEditMode ? (
                        <PropertyAttributeForm />
                    ) : (
                        <PropertyAttribute propertyId={propertyId} onEdit={onEdit} />
                    )}
                </Box>
            </Box>
            <Box
                justifyContent="flex-end"
                paddingTop={3}
            >
                {isEditMode && (
                    <Button
                        id="btnCreateProperty"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={onSave}
                        disabled={!isFormValid}
                    >
                        Save
                    </Button>
                )}
            </Box>
            <Box
                display="grid"
                gridTemplateColumns={isOneColumnLayout ? "1fr" : "1fr 1fr"}
                gap={1}
                py={2}
            >
                <Typography variant="h6" gridColumn="1 / -1">
                    Issue Dashboards
                </Typography>
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <LanguageIcon color="inherit" fontSize="large" />
                            <Box>
                                <Typography variant="h6" fontWeight={600}>
                                    Public Dashboard
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    Public facing dashboard for external stakeholders
                                </Typography>
                            </Box>
                        </Box>
                        <CodeBuildJobList />
                    </CardContent>
                </Card>
                <Card>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'space-between', height: '100%' }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <LockOutlinedIcon color="inherit" fontSize="large" />
                            <Box>
                                <Typography variant="h6" fontWeight={600}>
                                    Dashboard (Private)
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    Preview the latest dashboard in a private environment
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => {
                                window.open(`/properties/${propertyId}/dashboard`, '_blank');
                            }}
                        >
                            Preview Latest Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </Box>
            <Box py={2}>
                <RecordingList />
            </Box>
            {property?.isTestProperty && (
                <Box>
                    <Chip color="warning" label="Test Property" />
                </Box>
            )}
            <ManageResourcePermissionDialog
                onClose={() => setIsOpenShareDialog(false)}
                open={isOpenShareDialog}
                resourceId={propertyId}
                resourceType={PermissionResourceType.PROPERTY}
            />
            <ArchivePropertyDialog
                isOpen={isArchiveDialogOpen}
                onClose={() => setIsArchiveDialogOpen(false)}
                onArchive={async () => {
                    const updatedProperty = await propertyDaoRef.current.editProperty(propertyId, { ...property!, isArchived: true });
                    setProperty(updatedProperty);
                    setFormState(updatedProperty);
                    setIsArchiveDialogOpen(false);
                }}
            />
        </>
    );
};

export default PropertyInfo;
